import { type ReactNode, useMemo } from 'react'
import {
  daysBetween,
  effectiveMastery,
  retrievability,
} from '@/lib/recommendation/retrievability'
import { trpc } from '@/lib/trpc'
import { rollUpTopicMastery } from './_lib/derivations'
import { mapCoachAdjustmentsToCalcLogEntries } from './_lib/map-coach-adjustment-log'
import { RecommendationEngineProvider } from './recommendation-engine-context'
import {
  RecommendationEngineMissingData,
  RecommendationEngineNoData,
} from './recommendation-engine-data-placeholders'
import type { RecommendationEngineDataState } from './recommendation-engine-data-types'

interface RecommendationEngineStagedProviderProps {
  children: ReactNode
  clientId?: string | null
  baselineEpochAt?: Date | string | null
  baselineEpochTimeZone?: string | null
  engineEnabled?: boolean
  onSetBaselineEpoch?: (date: string) => Promise<void>
  onUpdateBaselineEpoch?: (date: string) => Promise<void>
  onClearBaselineEpoch?: () => Promise<void>
}

interface RecommendationEngineCatalogBridgeProps {
  clientId: string | null
  children: ReactNode
  baselineEpochAt?: Date | string | null
  baselineEpochTimeZone?: string | null
  engineEnabled?: boolean
  onSetBaselineEpoch?: (date: string) => Promise<void>
  onUpdateBaselineEpoch?: (date: string) => Promise<void>
  onClearBaselineEpoch?: () => Promise<void>
}

type ProficiencyGoalsStateData = {
  presets: {
    id: 'standard' | 'apple' | 'google' | 'meta'
    name: string
    minProficiencyThreshold: number
    maxProficiencyThreshold: number
  }[]
  topics: {
    id: string
    name: string
    slug: string
    minProficiencyThreshold: number
    maxProficiencyThreshold: number
    isEnabled?: boolean
    subtopics: {
      id: string
      name: string
      slug: string
      mastery: number
      stabilityDays: number
      difficulty: number
      lastAttemptAt: string | null
      minProficiencyThreshold: number
      maxProficiencyThreshold: number
      rollupWeight: number
      isEnabled: boolean
    }[]
  }[]
}

type SetupStateData = {
  clientId: string
  baselineEpochAt: Date | string | null
  baselineEpochTimeZone: string | null
  catalog: { topicCount: number; subtopicCount: number }
  state: {
    topicStateCount: number
    subtopicStateCount: number
    hasSeedData: boolean
  }
  latestSeed: {
    id: string
    bucket: 'knows_nothing' | 'beginner' | 'intermediate' | 'advanced'
    occurredAt: Date
    coachName: string
    hasPendingProcessing: boolean
  } | null
  persistedSubtopicStates: Record<
    string,
    {
      mastery: number
      stabilityDays: number
      difficulty: number
      isEnabled: boolean
    }
  >
}

function RecommendationEngineCatalogBridge({
  clientId,
  children,
  baselineEpochAt,
  baselineEpochTimeZone,
  engineEnabled,
  onSetBaselineEpoch,
  onUpdateBaselineEpoch,
  onClearBaselineEpoch,
}: RecommendationEngineCatalogBridgeProps) {
  const hasClient = clientId !== null

  const setupStateQuery =
    trpc.admin.recommendationEngine.getSetupState.useQuery(
      { clientId: clientId ?? '' },
      { enabled: hasClient },
    )
  const topicCatalogQuery =
    trpc.admin.recommendationEngine.getTopicCatalog.useQuery(undefined, {
      enabled: hasClient,
    })
  const companyPresetsQuery =
    trpc.admin.recommendationEngine.getCompanyProficiencyPresets.useQuery(
      undefined,
      { enabled: hasClient },
    )
  const seedMutation =
    trpc.admin.recommendationEngine.seedKnowledgeMap.useMutation()
  const runPendingProcessingMutation =
    trpc.admin.recommendationEngine.runPendingProcessing.useMutation()
  const saveGraphJsonEditorStateMutation =
    trpc.admin.recommendationEngine.saveGraphJsonEditorState.useMutation()
  const revertGraphJsonEditorStateMutation =
    trpc.admin.recommendationEngine.revertGraphJsonEditorState.useMutation()
  const graphJsonEditorStateQuery =
    trpc.admin.recommendationEngine.getGraphJsonEditorState.useQuery(
      { clientId: clientId ?? '' },
      { enabled: hasClient },
    )
  const calculationLogQuery =
    trpc.admin.recommendationEngine.getCalculationLog.useQuery(
      { clientId: clientId ?? '' },
      { enabled: hasClient },
    )
  const proficiencyGoalsStateQuery =
    trpc.admin.recommendationEngine.getProficiencyGoalsState.useQuery(
      { clientId: clientId ?? '' },
      { enabled: hasClient },
    )
  const updateProficiencyThresholdsMutation =
    trpc.admin.recommendationEngine.updateProficiencyThresholds.useMutation()
  const utils = trpc.useUtils()

  const resolvedClientId = clientId ?? ''
  const setupData = setupStateQuery.data as SetupStateData | undefined
  const latestSeed = setupData?.latestSeed

  const seedKnowledgeMap = async (payload: {
    bucket: 'knows_nothing' | 'beginner' | 'intermediate' | 'advanced'
    subtopicOverrides?: Record<
      string,
      {
        mastery?: number
        stabilityDays?: number
        difficulty?: number
        isEnabled?: boolean
      }
    >
  }) => {
    const result = await seedMutation.mutateAsync({
      clientId: resolvedClientId,
      bucket: payload.bucket,
      acknowledgedWarning: true,
      subtopicOverrides: payload.subtopicOverrides,
    })
    await Promise.all([
      utils.admin.recommendationEngine.getSetupState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getProgressState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getProficiencyGoalsState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getCalculationLog.invalidate({
        clientId: resolvedClientId,
      }),
    ])
    await utils.admin.recommendationEngine.getSetupState.fetch({
      clientId: resolvedClientId,
    })
    return {
      isReseed: result?.isReseed,
      hasPendingProcessing: result?.hasPendingProcessing,
    }
  }

  const onRunPendingProcessing = async () => {
    await runPendingProcessingMutation.mutateAsync({
      clientId: resolvedClientId,
    })
    await Promise.all([
      utils.admin.recommendationEngine.getSetupState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getProgressState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getProficiencyGoalsState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getCalculationLog.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getRecommendationsState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getHomeworkReviewState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getGraphJsonEditorState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getGraphRecommendationsState.invalidate({
        clientId: resolvedClientId,
      }),
    ])
  }

  const revertGraphJsonEditorState = async (adjustmentId: string) => {
    await revertGraphJsonEditorStateMutation.mutateAsync({
      clientId: resolvedClientId,
      adjustmentId,
    })
    await Promise.all([
      utils.admin.recommendationEngine.getGraphJsonEditorState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getGraphRecommendationsState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getCalculationLog.invalidate({
        clientId: resolvedClientId,
      }),
    ])
  }

  const saveGraphJsonEditorState = async (payload: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  }) => {
    await saveGraphJsonEditorStateMutation.mutateAsync({
      clientId: resolvedClientId,
      payload,
    })
    await Promise.all([
      utils.admin.recommendationEngine.getGraphJsonEditorState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getGraphRecommendationsState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getCalculationLog.invalidate({
        clientId: resolvedClientId,
      }),
    ])
  }

  const updateProficiencyThresholds = async (payload: {
    topicUpdates?: {
      id: string
      minProficiencyThreshold?: number
      maxProficiencyThreshold?: number
      rollupWeight?: number
      isEnabled?: boolean
    }[]
    subtopicUpdates?: {
      id: string
      minProficiencyThreshold?: number
      maxProficiencyThreshold?: number
      rollupWeight?: number
      isEnabled?: boolean
    }[]
  }) => {
    await updateProficiencyThresholdsMutation.mutateAsync({
      clientId: resolvedClientId,
      topicUpdates: payload.topicUpdates,
      subtopicUpdates: payload.subtopicUpdates,
    })
    await Promise.all([
      utils.admin.recommendationEngine.getProficiencyGoalsState.invalidate({
        clientId: resolvedClientId,
      }),
      utils.admin.recommendationEngine.getCalculationLog.invalidate({
        clientId: resolvedClientId,
      }),
    ])
  }

  const goalsTopics =
    (proficiencyGoalsStateQuery.data as ProficiencyGoalsStateData | undefined)
      ?.topics ?? []

  const mappedProficiencyGoalsTopics = useMemo(() => {
    const now = new Date()
    return goalsTopics.map((topic) => {
      const mappedSubtopics = topic.subtopics.map((subtopic) => {
        const elapsedDays = daysBetween(subtopic.lastAttemptAt, now)
        const currentRetrievability = retrievability(
          elapsedDays,
          subtopic.stabilityDays,
        )
        return {
          id: subtopic.id,
          slug: subtopic.slug,
          name: subtopic.name,
          mastery: subtopic.mastery,
          stabilityDays: subtopic.stabilityDays,
          difficulty: subtopic.difficulty,
          retrievability: currentRetrievability,
          effectiveMastery: Math.round(
            effectiveMastery(
              subtopic.mastery,
              elapsedDays,
              subtopic.stabilityDays,
            ),
          ),
          lastAttemptAt: subtopic.lastAttemptAt,
          isEnabled: subtopic.isEnabled,
          minProficiencyThreshold: subtopic.minProficiencyThreshold,
          maxProficiencyThreshold: subtopic.maxProficiencyThreshold,
          rollupWeight: subtopic.rollupWeight,
        }
      })
      const includedInPlan = topic.isEnabled ?? true
      const topicMastery = rollUpTopicMastery(mappedSubtopics, {
        includedInPlan,
      })

      return {
        id: topic.id,
        slug: topic.slug,
        name: topic.name,
        chapter: null as number | null,
        minProficiencyThreshold: topic.minProficiencyThreshold,
        maxProficiencyThreshold: topic.maxProficiencyThreshold,
        topicMastery,
        prerequisiteIds: [],
        prerequisitesMet: true,
        includedInPlan,
        subtopics: mappedSubtopics,
      }
    })
  }, [goalsTopics])

  const calcLogItems = calculationLogQuery.data?.items ?? []
  const mappedCalculationLog = useMemo(
    () => mapCoachAdjustmentsToCalcLogEntries(calcLogItems),
    [calcLogItems],
  )

  if (!hasClient) {
    return (
      <RecommendationEngineProvider
        baselineEpochAt={baselineEpochAt}
        baselineEpochTimeZone={baselineEpochTimeZone}
        engineEnabled={engineEnabled}
        onSetBaselineEpoch={onSetBaselineEpoch}
        onUpdateBaselineEpoch={onUpdateBaselineEpoch}
        onClearBaselineEpoch={onClearBaselineEpoch}
      >
        {children}
      </RecommendationEngineProvider>
    )
  }

  return (
    <RecommendationEngineProvider
      baselineEpochAt={baselineEpochAt}
      baselineEpochTimeZone={baselineEpochTimeZone}
      engineEnabled={engineEnabled}
      onSetBaselineEpoch={onSetBaselineEpoch}
      onUpdateBaselineEpoch={onUpdateBaselineEpoch}
      onClearBaselineEpoch={onClearBaselineEpoch}
      catalogTopics={topicCatalogQuery.data?.topics}
      catalogTopicCount={topicCatalogQuery.data?.topicCount}
      catalogSubtopicCount={topicCatalogQuery.data?.subtopicCount}
      companyProficiencyPresets={
        (
          proficiencyGoalsStateQuery.data as
            | ProficiencyGoalsStateData
            | undefined
        )?.presets ?? companyPresetsQuery.data?.presets
      }
      proficiencyGoalsTopics={mappedProficiencyGoalsTopics}
      onUpdateProficiencyThresholds={updateProficiencyThresholds}
      onSeedKnowledgeMap={seedKnowledgeMap}
      initialKnowledgeMapSeeded={setupData?.state.hasSeedData ?? false}
      initialSeedBucket={latestSeed?.bucket ?? null}
      initialSeedOccurredAt={latestSeed?.occurredAt ?? null}
      initialSeedCoachName={latestSeed?.coachName ?? null}
      initialPendingSeedProcessing={latestSeed?.hasPendingProcessing ?? false}
      initialPersistedSeedSubtopicStates={
        setupData?.persistedSubtopicStates ?? {}
      }
      initialCalculationLog={mappedCalculationLog}
      initialGraphJsonBaseline={graphJsonEditorStateQuery.data?.baselinePayload}
      initialGraphJsonCurrent={graphJsonEditorStateQuery.data?.payload}
      pendingGraphJsonAdjustmentId={
        graphJsonEditorStateQuery.data?.pendingGraphJsonAdjustmentId ?? null
      }
      onRunPendingProcessing={onRunPendingProcessing}
      onSaveGraphJsonEditorState={saveGraphJsonEditorState}
      onRevertGraphJsonEditorState={revertGraphJsonEditorState}
    >
      {children}
    </RecommendationEngineProvider>
  )
}

function buildSetupPreview(
  setupState:
    | {
        catalog: { topicCount: number; subtopicCount: number }
        state: {
          hasSeedData: boolean
        }
      }
    | undefined,
) {
  const hasTopicCatalog = (setupState?.catalog.topicCount ?? 0) > 0
  const hasSubtopicCatalog = (setupState?.catalog.subtopicCount ?? 0) > 0
  if (!hasTopicCatalog || !hasSubtopicCatalog) {
    return {
      status: 'missing_data' as const,
      missingFields: ['TopicDefinition', 'SubtopicDefinition'],
    }
  }

  if (!setupState?.state.hasSeedData) {
    return {
      status: 'no_data' as const,
      reason: 'This client has not been seeded yet.',
    }
  }

  return { status: 'ready' as const }
}

function mapReadTabState(input: {
  clientId: string | null
  isPending: boolean
  isError: boolean
  catalog?: {
    topicCount?: number
    subtopicCount?: number
  }
  state?: {
    topicStateCount?: number
    subtopicStateCount?: number
  }
}): RecommendationEngineDataState<true> {
  if (input.clientId === null) {
    return { status: 'missing_data', missingFields: ['clientId'] }
  }
  if (input.isPending) return { status: 'loading' }
  if (input.isError) {
    return {
      status: 'error',
      message: 'Could not load recommendation-engine data.',
    }
  }

  const topicCount = input.catalog?.topicCount
  const subtopicCount = input.catalog?.subtopicCount
  if (
    (topicCount !== undefined && topicCount <= 0) ||
    (subtopicCount !== undefined && subtopicCount <= 0)
  ) {
    return {
      status: 'missing_data',
      missingFields: ['TopicDefinition', 'SubtopicDefinition'],
    }
  }

  const topicStateCount = input.state?.topicStateCount ?? 0
  const subtopicStateCount = input.state?.subtopicStateCount ?? 0
  if (topicStateCount <= 0 || subtopicStateCount <= 0) {
    return {
      status: 'no_data',
      reason:
        'No seeded recommendation-engine state is available yet for this client.',
    }
  }

  return { status: 'ready', data: true }
}

function SetupStateProbe({ clientId }: { clientId: string }) {
  const setupStateQuery =
    trpc.admin.recommendationEngine.getSetupState.useQuery(
      {
        clientId,
      },
      { enabled: true },
    )

  if (setupStateQuery.isPending) {
    return <RecommendationEngineNoData message="Loading setup data..." />
  }

  if (setupStateQuery.isError) {
    return (
      <RecommendationEngineNoData message="Could not load setup data yet." />
    )
  }

  const preview = buildSetupPreview(setupStateQuery.data)
  if (preview.status === 'missing_data') {
    return <RecommendationEngineMissingData fields={preview.missingFields} />
  }
  if (preview.status === 'no_data') {
    return <RecommendationEngineNoData message={preview.reason} />
  }

  return null
}

function ProgressStateProbe({ clientId }: { clientId: string }) {
  const progressStateQuery =
    trpc.admin.recommendationEngine.getProgressState.useQuery(
      { clientId },
      { enabled: true },
    )

  const mapped = mapReadTabState({
    clientId,
    isPending: progressStateQuery.isPending,
    isError: progressStateQuery.isError,
    catalog: progressStateQuery.data?.catalog,
    state: progressStateQuery.data?.state,
  })

  if (mapped.status === 'missing_data') {
    return <RecommendationEngineMissingData fields={mapped.missingFields} />
  }
  if (mapped.status === 'loading') {
    return <RecommendationEngineNoData message="Loading progress data..." />
  }
  if (mapped.status === 'error') {
    return <RecommendationEngineNoData message={mapped.message} />
  }
  if (mapped.status === 'no_data') {
    return <RecommendationEngineNoData message={mapped.reason} />
  }

  return null
}

function ProficiencyGoalsStateProbe({ clientId }: { clientId: string }) {
  const goalsStateQuery =
    trpc.admin.recommendationEngine.getProficiencyGoalsState.useQuery(
      { clientId },
      { enabled: true },
    )

  const mapped = mapReadTabState({
    clientId,
    isPending: goalsStateQuery.isPending,
    isError: goalsStateQuery.isError,
    state: {
      topicStateCount: goalsStateQuery.data?.topics.length ?? 0,
      subtopicStateCount:
        goalsStateQuery.data?.topics.reduce(
          (sum, topic) => sum + topic.subtopics.length,
          0,
        ) ?? 0,
    },
  })

  if (mapped.status === 'missing_data') {
    return <RecommendationEngineMissingData fields={mapped.missingFields} />
  }
  if (mapped.status === 'loading') {
    return (
      <RecommendationEngineNoData message="Loading proficiency-goal data..." />
    )
  }
  if (mapped.status === 'error') {
    return <RecommendationEngineNoData message={mapped.message} />
  }
  if (mapped.status === 'no_data') {
    return <RecommendationEngineNoData message={mapped.reason} />
  }

  return null
}

function CalculationLogStateProbe({ clientId }: { clientId: string }) {
  const calculationLogQuery =
    trpc.admin.recommendationEngine.getCalculationLog.useQuery(
      { clientId },
      { enabled: true },
    )

  const mapped = mapReadTabState({
    clientId,
    isPending: calculationLogQuery.isPending,
    isError: calculationLogQuery.isError,
    state: {
      topicStateCount: calculationLogQuery.data?.items.length ?? 0,
      subtopicStateCount: calculationLogQuery.data?.items.length ?? 0,
    },
  })

  if (mapped.status === 'missing_data') {
    return <RecommendationEngineMissingData fields={mapped.missingFields} />
  }
  if (mapped.status === 'loading') {
    return <RecommendationEngineNoData message="Loading calculation log..." />
  }
  if (mapped.status === 'error') {
    return <RecommendationEngineNoData message={mapped.message} />
  }
  if (mapped.status === 'no_data') {
    return (
      <RecommendationEngineNoData message="No calculation-log rows are available yet." />
    )
  }

  return null
}

function RecommendationsStateProbe({ clientId }: { clientId: string }) {
  const recommendationsStateQuery =
    trpc.admin.recommendationEngine.getRecommendationsState.useQuery(
      { clientId },
      { enabled: true },
    )

  const mapped = mapReadTabState({
    clientId,
    isPending: recommendationsStateQuery.isPending,
    isError: recommendationsStateQuery.isError,
    state: {
      topicStateCount:
        recommendationsStateQuery.data?.state.topicStateCount ?? 0,
      subtopicStateCount:
        recommendationsStateQuery.data?.state.subtopicStateCount ?? 0,
    },
  })

  if (mapped.status === 'missing_data') {
    return <RecommendationEngineMissingData fields={mapped.missingFields} />
  }
  if (mapped.status === 'loading') {
    return (
      <RecommendationEngineNoData message="Loading recommendation data..." />
    )
  }
  if (mapped.status === 'error') {
    return <RecommendationEngineNoData message={mapped.message} />
  }
  if (mapped.status === 'no_data') {
    return (
      <RecommendationEngineNoData message="No recommendation inputs are available yet." />
    )
  }

  return null
}

function GraphRecommendationsStateProbe({ clientId }: { clientId: string }) {
  const graphStateQuery =
    trpc.admin.recommendationEngine.getGraphRecommendationsState.useQuery(
      { clientId },
      { enabled: true },
    )

  const mapped = mapReadTabState({
    clientId,
    isPending: graphStateQuery.isPending,
    isError: graphStateQuery.isError,
    state: {
      topicStateCount: graphStateQuery.data?.state.topicStateCount ?? 0,
      subtopicStateCount: graphStateQuery.data?.state.subtopicStateCount ?? 0,
    },
  })

  if (mapped.status === 'missing_data') {
    return <RecommendationEngineMissingData fields={mapped.missingFields} />
  }
  if (mapped.status === 'loading') {
    return (
      <RecommendationEngineNoData message="Loading graph recommendation data..." />
    )
  }
  if (mapped.status === 'error') {
    return <RecommendationEngineNoData message={mapped.message} />
  }
  if (mapped.status === 'no_data') {
    return (
      <RecommendationEngineNoData message="No graph recommendation inputs are available yet." />
    )
  }

  return null
}

function HomeworkReviewStateProbe({ clientId }: { clientId: string }) {
  const homeworkStateQuery =
    trpc.admin.recommendationEngine.getHomeworkReviewState.useQuery(
      { clientId },
      { enabled: true },
    )

  if (homeworkStateQuery.isPending) {
    return (
      <RecommendationEngineNoData message="Loading homework-review data..." />
    )
  }
  if (homeworkStateQuery.isError) {
    return (
      <RecommendationEngineNoData message="Could not load homework-review data." />
    )
  }
  if ((homeworkStateQuery.data?.state.attemptCount ?? 0) <= 0) {
    return (
      <RecommendationEngineNoData message="No completed attempts are available for review yet." />
    )
  }

  return null
}

/**
 * Staged provider: mounts real-data readiness probes and wraps the runtime store.
 *
 * Probes run eagerly today so each tab's placeholder can render without waiting for
 * navigation; tab-scoped lazy probing is deferred to a follow-up optimization.
 */
export function RecommendationEngineStagedProvider({
  children,
  clientId = null,
  baselineEpochAt = null,
  baselineEpochTimeZone = null,
  engineEnabled = true,
  onSetBaselineEpoch,
  onUpdateBaselineEpoch,
  onClearBaselineEpoch,
}: RecommendationEngineStagedProviderProps) {
  return (
    <>
      {clientId === null ? (
        <RecommendationEngineMissingData fields={['clientId']} />
      ) : (
        <>
          <SetupStateProbe clientId={clientId} />
          <ProgressStateProbe clientId={clientId} />
          <ProficiencyGoalsStateProbe clientId={clientId} />
          <CalculationLogStateProbe clientId={clientId} />
          <RecommendationsStateProbe clientId={clientId} />
          <GraphRecommendationsStateProbe clientId={clientId} />
          <HomeworkReviewStateProbe clientId={clientId} />
        </>
      )}

      <RecommendationEngineCatalogBridge
        clientId={clientId}
        baselineEpochAt={baselineEpochAt}
        baselineEpochTimeZone={baselineEpochTimeZone}
        engineEnabled={engineEnabled}
        onSetBaselineEpoch={onSetBaselineEpoch}
        onUpdateBaselineEpoch={onUpdateBaselineEpoch}
        onClearBaselineEpoch={onClearBaselineEpoch}
      >
        {children}
      </RecommendationEngineCatalogBridge>
    </>
  )
}
